import { format } from 'date-fns';
import type { ReactElement } from 'react';

import { useHealth } from '../../../hooks/useHealth.js';
import { ApiClientError } from '../../../lib/api-client.js';
import { Button } from '../../ui/Button.js';
import { Card } from '../../ui/Card.js';
import { Spinner } from '../../ui/Spinner.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import styles from './ApiStatusCard.module.css';

/**
 * "About" card on Settings: whether the app can reach the API on the same origin, which
 * environment it is talking to and the running API version.
 */
export function ApiStatusCard(): ReactElement {
    const health = useHealth();

    if (health.isPending) {
        return (
            <Card title="About" headerEnd={<StatusBadge tone="neutral">Checking</StatusBadge>}>
                <div className={styles.loading}>
                    <Spinner size="sm" label="Checking service status" />
                    <span>Contacting the API...</span>
                </div>
            </Card>
        );
    }

    if (health.isError) {
        const error = health.error;
        const detail =
            error instanceof ApiClientError
                ? `${error.message} (${error.code}${error.requestId ? `, ref ${error.requestId}` : ''})`
                : 'The API could not be reached. Check your connection and try again.';
        return (
            <Card title="About" headerEnd={<StatusBadge tone="danger">Unavailable</StatusBadge>}>
                <p role="alert" className={styles.errorText}>
                    {detail}
                </p>
                <div>
                    <Button
                        variant="secondary"
                        loading={health.isFetching}
                        onClick={() => {
                            void health.refetch();
                        }}
                    >
                        Try again
                    </Button>
                </div>
            </Card>
        );
    }

    const { data } = health;
    return (
        <Card title="About" headerEnd={<StatusBadge tone="success">Connected</StatusBadge>}>
            <dl className={styles.details}>
                <div className={styles.row}>
                    <dt>Environment</dt>
                    <dd>{data.environment}</dd>
                </div>
                <div className={styles.row}>
                    <dt>API version</dt>
                    <dd className={styles.mono}>{data.version}</dd>
                </div>
                <div className={styles.row}>
                    <dt>Last checked</dt>
                    <dd>{format(new Date(data.timestamp), 'h:mm:ss a')}</dd>
                </div>
            </dl>
        </Card>
    );
}
